import { theme as primerTheme } from '@primer/react';
import deepmerge from 'deepmerge';
import { createGlobalStyle } from 'styled-components';

// Brand tokens shared with the cozyjuicyreal.com website
// (see cozyjuicyreal/src/index.css). Keep the two in sync.
export const brand = {
  purple: 'hsl(263, 84%, 33%)', // #440d9b
  purpleSoft: 'hsl(252, 100%, 71%)', // #896dff
  purpleDark: 'hsl(263, 84%, 22%)',
  orange: 'hsl(27, 100%, 50%)', // #ff7300
  yellow: 'hsl(51, 100%, 51%)', // #ffdb06
  lavender: 'hsl(252, 100%, 96%)', // #ede9ff
  cream: 'hsl(40, 40%, 98%)',
  ink: 'hsl(263, 50%, 12%)',
};

// Semantic tokens, named as on the website.
export const tokens = {
  background: 'hsl(40, 40%, 99%)',
  foreground: brand.ink,
  card: 'hsl(0, 0%, 100%)',
  primary: {
    DEFAULT: brand.purple,
    foreground: 'hsl(0, 0%, 100%)',
    soft: brand.purpleSoft,
    dark: brand.purpleDark,
    hover: 'hsl(263, 70%, 42%)',
  },
  secondary: {
    DEFAULT: brand.lavender,
    foreground: brand.purple,
  },
  muted: {
    DEFAULT: 'hsl(252, 40%, 96%)',
    foreground: 'hsl(263, 20%, 38%)',
  },
  accent: {
    DEFAULT: brand.orange,
    foreground: 'hsl(0, 0%, 100%)',
    mint: {
      bg: 'hsl(145, 100%, 96%)',
      border: 'hsl(145, 61%, 84%)',
      foreground: 'hsl(161, 88%, 20%)',
    },
    lilac: {
      bg: 'hsl(266, 100%, 97%)',
      border: 'hsl(257, 100%, 91%)',
    },
    peach: {
      bg: 'hsl(34, 100%, 94%)',
      bgSoft: 'hsl(29, 100%, 95%)',
      border: 'hsl(31, 100%, 82%)',
      borderStrong: 'hsl(32, 100%, 75%)',
      foreground: 'hsl(25, 100%, 24%)',
    },
  },
  highlight: {
    DEFAULT: brand.yellow,
    foreground: brand.ink,
  },
  destructive: {
    DEFAULT: 'hsl(0, 84%, 55%)',
    foreground: 'hsl(0, 0%, 100%)',
  },
  rating: 'hsl(160, 100%, 36%)',
  border: 'hsl(252, 30%, 90%)',
  input: 'hsl(252, 30%, 90%)',
  ring: brand.purple,
};

export const gradients = {
  hero: `radial-gradient(ellipse at top left, ${brand.lavender} 0%, transparent 55%),
    radial-gradient(ellipse at bottom right, hsla(27, 100%, 50%, 0.12) 0%, transparent 50%),
    ${tokens.background}`,
  purple: `linear-gradient(135deg, ${brand.purple} 0%, ${brand.purpleSoft} 100%)`,
  warm: `linear-gradient(135deg, ${brand.orange} 0%, ${brand.yellow} 100%)`,
};

export const fonts = {
  display: "'Sora', system-ui, sans-serif",
  body: "'Manrope', system-ui, -apple-system, sans-serif",
};

// Game-mode palettes, specific to the app.
export const colors = {
  accent: {
    fg: 'hsl(20, 90%, 65%)',
    emphasis: '#FF7300',
    muted: 'hsl(18, 100%, 75%)',
    subtle: 'hsl(16, 83%, 85%)',
    border: '#7C2000',
    text: '#d8651f',
  },
  cozy: {
    muted: '#f6faf1',
    bg: '#e7f1da',
    light: '#adcc63',
    medium: '#00aa98',
    dark: '#007369',
    text: '#004d46',
  },
  juicy: {
    muted: '#fffbef',
    bg: '#fff2cc',
    light: '#ffd22a',
    medium: '#f39851',
    dark: '#d8651f',
    darker: '#A53200',
    text: '#863f13',
  },
  real: {
    muted: '#fdf2ec',
    bg: '#FFF5F2',
    light: '#f68a53',
    btn: '#DB004F',
    medium: '#cd273a',
    dark: '#8b041b',
    text: '#8b041b',
  },
  random: {
    muted: '#ecf7f7',
    bg: '#dbf0f0',
    light: '#2886a7',
    medium: '#025a99',
    dark: '#154371',
    text: '#154371',
  },
  lightning: {
    muted: '#fef5f8',
    bg: '#fad1df',
    light: '#f5a1c5',
    medium: '#f5a1c5',
    dark: '#971b6d',
    text: '#971b6d',
  },
  spark: {
    muted: '#EDE9FF',
    bg: '#f2ebf5',
    light: '#e9ddee',
    medium: '#917cb0',
    new: '#BFAFFF',
    dark: '#440D9B',
    btn: '#896DFF',
    text: '#381f57',
    other: '#A792FF',
  },
};

// Purple-tinted shadows from the website. The elevation names are kept
// for existing themeGet() lookups.
const shadows = {
  highlight: 'inset 0 1px 0 hsla(0, 0%, 100%, 0.25)',
  inset: '0 0 transparent', // Primer puts this on text inputs; the website has none
  focus: '0 0 0 3px hsla(263, 84%, 33%, 0.3)',
  soft: '0 6px 24px -8px hsla(263, 84%, 33%, 0.18)',
  card: '0 12px 40px -16px hsla(263, 84%, 33%, 0.22)',
  glow: '0 20px 60px -20px hsla(263, 84%, 33%, 0.45)',
  elevationLow: '0 6px 24px -8px hsla(263, 84%, 33%, 0.18)',
  elevationMedium: '0 12px 40px -16px hsla(263, 84%, 33%, 0.22)',
  elevationHigh: '0 20px 60px -20px hsla(263, 84%, 33%, 0.45)',
};

// Website radius scale (--radius: 1rem). Numeric keys are what Primer
// components and sx props read: 1 = inputs and small surfaces,
// 2 = buttons and dialogs.
export const radii = {
  0: '0',
  1: '12px',
  2: '16px',
  3: '24px',
  4: '32px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  xxl: '32px',
  full: '9999px',
};

// Overrides & customises parts of Primer theme.
// See https://primer.style/react/theme-reference
export const theme = deepmerge(primerTheme, {
  fonts: {
    normal: fonts.body,
    display: fonts.display,
  },
  fontSizes: {
    0: '12px',
    1: '16px',
    2: '20px',
    3: '24px',
    4: '30px',
    5: '36px',
    6: '48px',
    7: '60px',
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
    '5xl': '48px',
    '6xl': '60px',
  },
  fontWeights: {
    light: 400, // Manrope is loaded from 400 up
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  radii,
  colorSchemes: {
    light: {
      colors: {
        fg: {
          default: tokens.foreground,
          muted: tokens.muted.foreground,
          subtle: tokens.muted.foreground, // input placeholders
        },
        canvas: {
          default: tokens.background,
        },
        border: {
          default: tokens.border,
        },
        selectMenu: {
          tapFocusBg: brand.lavender,
        },
        // As on the website: the main call to action is an orange pill,
        // the secondary one a pill with a purple outline.
        btn: {
          bg: tokens.background,
          border: brand.purple,
          hoverBg: brand.lavender,
          hoverBorder: brand.purple,
          selectedBg: brand.lavender,
          activeBg: brand.lavender,
          activeBorder: brand.purple,
          disabledBg: tokens.muted.DEFAULT,
          disabledBorder: tokens.border,
          focusBg: brand.lavender,
          focusBorder: brand.purple,
          text: brand.purple,
          primary: {
            bg: tokens.accent.DEFAULT,
            text: tokens.accent.foreground,
            hoverText: tokens.accent.foreground,
            border: tokens.accent.DEFAULT,
            hoverBg: 'hsl(27, 100%, 55%)',
            hoverBorder: 'hsl(27, 100%, 55%)',
            selectedBg: 'hsl(27, 100%, 45%)',
            disabledBg: tokens.accent.peach.borderStrong,
            disabledText: 'hsla(0, 0%, 100%, 0.85)',
            disabledBorder: tokens.accent.peach.borderStrong,
            focusBg: tokens.accent.DEFAULT,
            focusBorder: tokens.accent.DEFAULT,
            icon: tokens.accent.foreground,
          },
          outline: {
            text: brand.purple,
            border: brand.purple,
            hoverBg: brand.lavender,
            hoverText: brand.purple,
          },
        },
        brand,
        primary: tokens.primary,
        secondary: tokens.secondary,
        muted: tokens.muted,
        highlight: tokens.highlight,
        destructive: tokens.destructive,
        soft: tokens.accent,
        ...colors,
        // Primer draws links, checkboxes and focus rings from `accent`;
        // the website uses purple for those. The exported `colors.accent`
        // stays orange for the app's own components.
        accent: {
          ...colors.accent,
          fg: brand.purple,
          emphasis: brand.purple,
          muted: brand.lavender,
          subtle: brand.lavender,
        },
      },
      shadows: {
        btn: {
          // No shadows on default buttons: the outline is a solid 2px
          // border (see index.css), as on the website.
          shadow: '0 0 transparent',
          insetShadow: '0 0 transparent',
          focusShadow: shadows.focus,
          shadowInputFocus: `0 0 0 0.2em ${brand.lavender}`,
          primary: {
            shadow: shadows.soft,
            selectedShadow: shadows.soft,
            focusShadow: shadows.focus,
          },
          outline: {
            hoverShadow: '0 0 transparent',
            selectedShadow: '0 0 transparent',
          },
        },
        card: {
          shadow: shadows.soft,
        },
        cardButton: {
          shadow: shadows.soft,
          focusShadow: shadows.card,
        },
        primer: {
          shadow: shadows,
        },
        ...shadows,
      },
    },
  },
});

// Exposes the tokens as CSS variables (same names as the website) for
// plain CSS and styled-components.
export const GlobalTokens = createGlobalStyle`
  :root {
    --brand-purple: ${brand.purple};
    --brand-purple-soft: ${brand.purpleSoft};
    --brand-purple-dark: ${brand.purpleDark};
    --brand-orange: ${brand.orange};
    --brand-yellow: ${brand.yellow};
    --brand-lavender: ${brand.lavender};
    --brand-cream: ${brand.cream};
    --brand-ink: ${brand.ink};

    --background: ${tokens.background};
    --foreground: ${tokens.foreground};
    --card: ${tokens.card};
    --primary: ${tokens.primary.DEFAULT};
    --primary-foreground: ${tokens.primary.foreground};
    --secondary: ${tokens.secondary.DEFAULT};
    --secondary-foreground: ${tokens.secondary.foreground};
    --muted: ${tokens.muted.DEFAULT};
    --muted-foreground: ${tokens.muted.foreground};
    --accent: ${tokens.accent.DEFAULT};
    --accent-foreground: ${tokens.accent.foreground};
    --highlight: ${tokens.highlight.DEFAULT};
    --highlight-foreground: ${tokens.highlight.foreground};
    --destructive: ${tokens.destructive.DEFAULT};
    --destructive-foreground: ${tokens.destructive.foreground};
    --rating: ${tokens.rating};
    --border: ${tokens.border};
    --input: ${tokens.input};
    --ring: ${tokens.ring};
    --control-borderColor-rest: ${tokens.input};

    --accent-mint-bg: ${tokens.accent.mint.bg};
    --accent-mint-border: ${tokens.accent.mint.border};
    --accent-mint-foreground: ${tokens.accent.mint.foreground};
    --accent-lilac-bg: ${tokens.accent.lilac.bg};
    --accent-lilac-border: ${tokens.accent.lilac.border};
    --accent-peach-bg: ${tokens.accent.peach.bg};
    --accent-peach-bg-soft: ${tokens.accent.peach.bgSoft};
    --accent-peach-border: ${tokens.accent.peach.border};
    --accent-peach-border-strong: ${tokens.accent.peach.borderStrong};
    --accent-peach-foreground: ${tokens.accent.peach.foreground};

    --gradient-hero: ${gradients.hero};
    --gradient-purple: ${gradients.purple};
    --gradient-warm: ${gradients.warm};

    --shadow-soft: ${shadows.soft};
    --shadow-card: ${shadows.card};
    --shadow-glow: ${shadows.glow};

    --radius: ${radii.lg};
    --radius-sm: ${radii.sm};
    --radius-md: ${radii.md};
    --radius-xl: ${radii.xl};
    --radius-2xl: ${radii.xxl};
    --radius-full: ${radii.full};

    --font-display: ${fonts.display};
    --font-body: ${fonts.body};

    --ease-out-soft: cubic-bezier(0.22, 1, 0.36, 1);
  }
`;

// Border, corners and focus ring come from index.css, as on the website;
// this only keeps the website's 40px height.
export const textInputStyle = {
  height: '40px',
};

// Kept for existing sx spreads; buttons are flat pills now, so this only
// resets the old 3D border to the 2px outline.
export const buttonStyle = {
  borderWidth: '2px',
};

export const checkboxStyle = {
  borderRadius: '4px',
  borderColor: brand.purple,
};
